import React from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Alert,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  LocalStage,
  LocalReasonSolution,
  EditingReasonSolution,
  FollowUpGroup,
} from "../types";
import {
  stagesContainerStyles,
  stagesTitleStyles,
  stageAccordionStyles,
} from "../styles";
import { ReasonSolutionForm } from "./ReasonSolutionForm";
import { StageEditForm } from "./StageEditForm";

interface StageListProps {
  stages: LocalStage[];
  reasonSolutions: LocalReasonSolution[];
  editingStage: any;
  stageError: string;
  onAddReasonSolutionClick: (stageId: string) => void;
  onEditStage: (stage: LocalStage) => void;
  onDeleteStage: (stageId: string) => void;
  onEditReasonSolution: (rs: LocalReasonSolution) => void;
  onDeleteReasonSolution: (reasonSolutionId: string) => void;
  getReasonSolutionsForStage: (stageId: string) => LocalReasonSolution[];
  // Reason Solution Form props
  editingReasonSolution: EditingReasonSolution;
  reasonSolutionError: string;
  onSaveReasonSolution: () => void;
  onCancelEditingReasonSolution: () => void;
  onRowChange: (value: number) => void;
  onIsPrimaryChange: (value: boolean) => void;
  onReasonChange: (value: string) => void;
  onSolutionChange: (value: string) => void;
  onPreviousReasonIdsChange: (value: string) => void;
  // Stage Edit Form props
  onSaveStage: () => void;
  onCancelEditingStage: () => void;
  onStageNumberChange: (value: number) => void;
  onTitleChange: (value: string) => void;
  onIsPublicChange: (value: boolean) => void;
  onFollowUpGroupIdsChange: (value: string) => void;
  followUpGroups: FollowUpGroup[];
  children?: React.ReactNode;
}

export const StageList: React.FC<StageListProps> = ({
  stages,
  reasonSolutions,
  editingStage,
  stageError,
  onAddReasonSolutionClick,
  onEditStage,
  onDeleteStage,
  onEditReasonSolution,
  onDeleteReasonSolution,
  getReasonSolutionsForStage,
  // Reason Solution Form props
  editingReasonSolution,
  reasonSolutionError,
  onSaveReasonSolution,
  onCancelEditingReasonSolution,
  onRowChange,
  onIsPrimaryChange,
  onReasonChange,
  onSolutionChange,
  onPreviousReasonIdsChange,
  // Stage Edit Form props
  onSaveStage,
  onCancelEditingStage,
  onStageNumberChange,
  onTitleChange,
  onIsPublicChange,
  onFollowUpGroupIdsChange,
  followUpGroups,
  children,
}) => {
  if (stages.length === 0) {
    return null;
  }

  return (
    <Box sx={stagesContainerStyles}>
      <Typography variant="subtitle2" sx={stagesTitleStyles}>
        مراحل:
      </Typography>
      {stages.map((stage) => (
        <Accordion key={stage.id} sx={stageAccordionStyles}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`stage-${stage.id}-content`}
            id={`stage-${stage.id}-header`}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
              }}
            >
              {editingStage.stageId === stage.id ? (
                // Editing mode - will be handled by StageEditForm
                <Typography variant="body2">در حال ویرایش...</Typography>
              ) : (
                // Display mode
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="body2">
                      مرحله {stage.stageNumber}: {stage.title}
                      {!stage.isPublic && (
                        <Box
                          component="span"
                          sx={{
                            ml: 1,
                            px: 0.5,
                            py: 0.25,
                            bgcolor: "warning.main",
                            color: "white",
                            borderRadius: 0.5,
                            fontSize: "0.7rem",
                          }}
                        >
                          خصوصی
                        </Box>
                      )}
                    </Typography>
                    {!stage.isPublic && stage.followUpGroupIdsJson && (
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {(() => {
                          try {
                            const ids = JSON.parse(stage.followUpGroupIdsJson);
                            return ids.map((id: number) => {
                              const group = followUpGroups.find(
                                (g) => g.id === id
                              );
                              return group ? (
                                <Box
                                  key={id}
                                  sx={{
                                    px: 0.5,
                                    py: 0.25,
                                    bgcolor: "primary.main",
                                    color: "white",
                                    borderRadius: 0.5,
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  {group.title}
                                </Box>
                              ) : null;
                            });
                          } catch {
                            return null;
                          }
                        })()}
                      </Box>
                    )}
                  </Box>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddReasonSolutionClick(stage.id);
                    }}
                    size="small"
                    color="secondary"
                    title="افزودن علت و راه حل"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditStage(stage);
                    }}
                    size="small"
                    color="primary"
                    title="ویرایش مرحله"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteStage(stage.id);
                    }}
                    size="small"
                    color="error"
                    title="حذف مرحله"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {editingStage.stageId === stage.id && stageError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {stageError}
              </Alert>
            )}

            {/* Stage Edit Form - Only show for this specific stage */}
            {editingStage.stageId === stage.id && (
              <StageEditForm
                editingStage={editingStage}
                stageError={stageError}
                onSave={onSaveStage}
                onCancel={onCancelEditingStage}
                onStageNumberChange={onStageNumberChange}
                onTitleChange={onTitleChange}
                onIsPublicChange={onIsPublicChange}
                onFollowUpGroupIdsChange={onFollowUpGroupIdsChange}
                followUpGroups={followUpGroups}
                isNewStage={false}
              />
            )}

            {/* Reason Solutions for this stage */}
            <ReasonSolutionsList
              stageId={stage.id}
              reasonSolutions={getReasonSolutionsForStage(stage.id)}
              onEditReasonSolution={onEditReasonSolution}
              onDeleteReasonSolution={onDeleteReasonSolution}
            />

            {/* Reason Solution Form - Only show for this specific stage */}
            {editingReasonSolution.stageId === stage.id && (
              <ReasonSolutionForm
                editingReasonSolution={editingReasonSolution}
                reasonSolutionError={reasonSolutionError}
                isEditing={!!editingReasonSolution.reasonSolutionId}
                onSave={onSaveReasonSolution}
                onCancel={onCancelEditingReasonSolution}
                onRowChange={onRowChange}
                onIsPrimaryChange={onIsPrimaryChange}
                onReasonChange={onReasonChange}
                onSolutionChange={onSolutionChange}
                onPreviousReasonIdsChange={onPreviousReasonIdsChange}
              />
            )}

            {/* Stage-specific content (forms, etc.) */}
            {children}

            <Typography variant="body2" color="text.secondary">
              جزئیات مرحله {stage.stageNumber}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

// Reason Solutions List Component
interface ReasonSolutionsListProps {
  stageId: string;
  reasonSolutions: LocalReasonSolution[];
  onEditReasonSolution: (rs: LocalReasonSolution) => void;
  onDeleteReasonSolution: (reasonSolutionId: string) => void;
}

const ReasonSolutionsList: React.FC<ReasonSolutionsListProps> = ({
  stageId,
  reasonSolutions,
  onEditReasonSolution,
  onDeleteReasonSolution,
}) => {
  if (reasonSolutions.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
        علت‌ها و راه‌حل‌ها:
      </Typography>
      {reasonSolutions.map((rs) => (
        <ReasonSolutionItem
          key={rs.id}
          reasonSolution={rs}
          onEdit={onEditReasonSolution}
          onDelete={onDeleteReasonSolution}
        />
      ))}
    </Box>
  );
};

// Reason Solution Item Component
interface ReasonSolutionItemProps {
  reasonSolution: LocalReasonSolution;
  onEdit: (rs: LocalReasonSolution) => void;
  onDelete: (reasonSolutionId: string) => void;
}

const ReasonSolutionItem: React.FC<ReasonSolutionItemProps> = ({
  reasonSolution,
  onEdit,
  onDelete,
}) => {
  return (
    <Box
      sx={{
        mb: 1,
        p: 1,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: "bold", minWidth: 60 }}>
          ردیف {reasonSolution.row}
        </Typography>

        {reasonSolution.isPrimary && (
          <Box
            component="span"
            sx={{
              px: 1,
              py: 0.5,
              bgcolor: "primary.main",
              color: "white",
              borderRadius: 1,
              fontSize: "0.75rem",
            }}
          >
            اصلی
          </Box>
        )}

        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          -
        </Typography>

        <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 150 }}>
          {reasonSolution.reason}
        </Typography>

        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          -
        </Typography>

        <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 150 }}>
          {reasonSolution.solution}
        </Typography>

        {reasonSolution.previousReasonIdsJson && (
          <>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              -
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 100 }}
            >
              {reasonSolution.previousReasonIdsJson}
            </Typography>
          </>
        )}

        <IconButton
          onClick={() => onEdit(reasonSolution)}
          size="small"
          color="primary"
          title="ویرایش"
        >
          <EditIcon fontSize="small" />
        </IconButton>

        <IconButton
          onClick={() => onDelete(reasonSolution.id)}
          size="small"
          color="error"
          title="حذف"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};
